import React, { useState } from 'react';
import { useStore, uid } from '../state/store';
import {
  Card, Chip, Banner, Empty, Money, Text, SectionHead, Stat, BarRow,
} from '../components/ui';
import { ExpenseForm } from '../components/Forms';
import { EXPENSE_CATEGORIES, EMPTY_COPY } from '../lib/constants';
import { fmtMoney, fmtMonths, num, describeRunway } from '../lib/derive';
import { fmtDate } from '../lib/dates';
import { orgTimeTargets } from '../lib/orgPlan';

export default function Runway() {
  const { state, d, set, add, patch, remove } = useStore();
  const { runway, sprint } = d;
  const cur = state.settings?.currency;
  const [open, setOpen] = useState(false);

  const byCat = {};
  for (const c of EXPENSE_CATEGORIES) byCat[c.key] = 0;
  for (const e of state.expenses || []) byCat[e.category] = (byCat[e.category] || 0) + num(e.amount);
  const total = Object.values(byCat).reduce((a, b) => a + b, 0);

  const tone = { healthy: 'green', watch: 'amber', critical: 'red', unknown: '' }[runway.status];
  const copy = describeRunway(runway);

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>Runway</h2>
          <Chip tone={tone}>{copy.primary}</Chip>
        </div>
        <div className="sub">
          {copy.secondary || 'Cash is time. Every material expense should name the evidence it is buying.'}
        </div>
      </div>

      <section className="grid g-2-1">
        <Card title="Position">
          <div className="grid g3">
            <Money label="Starting cash" currency={cur} value={state.runway.startingCash} onChange={(v) => set('runway.startingCash', v)} term="startingCash" />
            <Money label="Cash on hand" currency={cur} value={state.runway.cashToday} onChange={(v) => set('runway.cashToday', v)} term="cashOnHand" />
            <Money label="Expected proof spend" currency={cur} value={state.runway.expectedProofSpend} onChange={(v) => set('runway.expectedProofSpend', v)} term="expectedProofSpend" />
            <Money label="Current monthly burn" currency={cur} value={state.runway.monthlyBurn} onChange={(v) => set('runway.monthlyBurn', v)} term="currentMonthlyBurn" />
            <Money label="Minimum viable burn" currency={cur} value={state.runway.minViableBurn} onChange={(v) => set('runway.minViableBurn', v)} term="minViableBurn" />
            <Money label="Burn at sprint start" currency={cur} value={state.runway.baselineMonthlyBurn} onChange={(v) => set('runway.baselineMonthlyBurn', v)} term="burnAtSprintStart" />
          </div>
        </Card>

        <Card title="Derived">
          <Stat
            label="Current runway"
            term="currentRunway"
            value={copy.primary}
            size="lg"
            foot={
              runway.months == null
                ? copy.secondary
                : runway.runwayEndsOn
                  ? `Cash out around ${fmtDate(runway.runwayEndsOn, { year: true })}`
                  : Number.isFinite(runway.months)
                    ? 'Set a burn figure'
                    : 'Confirmed-zero burn — runway is unbounded at current spend'
            }
          />
          <div className="hr" />
          <Stat
            label="Minimum-burn runway"
            term="minBurnRunway"
            value={runway.minMonths != null ? fmtMonths(runway.minMonths) : 'Unknown'}
            size="sm"
            foot={runway.minBurnUnknown ? 'Missing: minimum viable monthly burn' : undefined}
          />
          <div className="hr" />
          <Stat label="Cash saved during sprint" term="cashSavedSprint" value={<span className="num">{fmtMoney(runway.cashSaved, cur)}</span>} size="sm" foot={`From burn reduction over ${sprint.daysCompleted} day(s)`} />
          {runway.survivesSprint === false && (
            <div style={{ marginTop: 12 }}>
              <Banner tone="stop">Current cash and burn do not reach {fmtDate(sprint.end)}.</Banner>
            </div>
          )}
        </Card>
      </section>

      <section className="section">
        <SectionHead title="Where the money goes" hint="KEEP survives. PROVE buys evidence. EXTEND buys time. STOP is everything else." />
        <div className="grid g4">
          {EXPENSE_CATEGORIES.map((c) => (
            <Card
              key={c.key}
              title={c.label}
              term={
                c.key === 'KEEP' ? 'expenseKeep'
                  : c.key === 'PROVE' ? 'expenseProve'
                    : c.key === 'EXTEND' ? 'expenseExtend'
                      : c.key === 'STOP' ? 'expenseStop'
                        : undefined
              }
              right={<span className="dim tiny">{total ? Math.round((byCat[c.key] / total) * 100) : 0}%</span>}
            >
              <Stat label={c.desc} value={<span className="num">{fmtMoney(byCat[c.key], cur)}</span>} size="sm" />
            </Card>
          ))}
        </div>
        {byCat.STOP > 0 && (
          <div style={{ marginTop: 10 }}>
            <Banner tone="stop">
              {fmtMoney(byCat.STOP, cur)} is classified STOP and is still being spent. Classification is not a decision.
            </Banner>
          </div>
        )}
      </section>

      <section className="section">
        <SectionHead
          title="Expenses"
          hint="Amount, category, opportunity, evidence objective, review date."
          right={<button className="btn sm primary" onClick={() => setOpen(true)}>Record spend</button>}
        />
        {(state.expenses || []).length === 0 ? (
          <Empty title={EMPTY_COPY.expenses}>Start with the recurring costs. Those are the ones that quietly decide the sprint.</Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Date</th>
                    <th>Item</th>
                    <th className="num" style={{ width: 120 }}>Amount</th>
                    <th style={{ width: 110 }}>Category</th>
                    <th style={{ width: 120 }}>Linked to</th>
                    <th>Evidence objective</th>
                    <th style={{ width: 100 }}>Review</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {[...state.expenses].sort((a, b) => (a.date < b.date ? 1 : -1)).map((e) => {
                    const flagged = runway.unjustified.some((u) => u.id === e.id);
                    return (
                      <tr key={e.id} style={flagged ? { background: 'var(--red-bg)' } : undefined}>
                        <td className="num">{fmtDate(e.date)}</td>
                        <td>{e.label}</td>
                        <td className="num">{fmtMoney(e.amount, cur)}</td>
                        <td>
                          <select value={e.category} onChange={(ev) => patch('expenses', e.id, { category: ev.target.value })}>
                            {EXPENSE_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                          </select>
                        </td>
                        <td>{e.linkedTo || <span className="dim">—</span>}</td>
                        <td>
                          {e.evidenceObjective || <span className="chip red">No evidence objective</span>}
                        </td>
                        <td className="num">{e.reviewDate || '—'}</td>
                        <td className="actions">
                          <button className="btn sm ghost" onClick={() => remove('expenses', e.id)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <FounderTime />

      {open && <ExpenseForm onClose={() => setOpen(false)} />}
    </>
  );
}

function FounderTime() {
  const { state, d, add, patch } = useStore();
  const { time, sprint } = d;
  const timeTargets = orgTimeTargets(state);
  const week = time.week;

  const ensureWeek = () => {
    if (!week) add('timeLog', { id: uid('tl'), weekStart: sprint.weekStart, dubai: 0, saudi: 0, infrastructure: 0, fundraising: 0, other: 0 });
  };

  return (
    <section className="section">
      <SectionHead
        title="Founder time tracker"
        hint={`Week of ${fmtDate(sprint.weekStart, { year: true })} · target vs actual`}
        right={!week ? <button className="btn sm" onClick={ensureWeek}>Start this week</button> : <span className="dim tiny">{time.totalHours} hours logged</span>}
      />
      <Card>
        {!week ? (
          <Empty title="No time data recorded">
            Without hours there is no way to test whether attention matches strategy. Allocation % stays hidden until a week is logged.
          </Empty>
        ) : !timeTargets.length ? (
          <Empty title="Plan not set">
            Time allocation targets come from your organization plan. Activate a template on Strategy first.
          </Empty>
        ) : (
          <>
            <div className="grid g5" style={{ marginBottom: 14 }}>
              {timeTargets.map((t) => (
                <div className="field" key={t.key} style={{ marginBottom: 0 }}>
                  <label>{t.label}</label>
                  <input
                    type="number"
                    className="num-input"
                    min="0"
                    value={week[t.key] ?? 0}
                    onChange={(e) => patch('timeLog', week.id, { [t.key]: Number(e.target.value) || 0 })}
                  />
                </div>
              ))}
            </div>
            <div className="hr" />
            {time.hasMinimumHours && time.rows.map((r) => (
              <BarRow key={r.key} label={`${r.label} · ${r.hours}h`} actual={r.actual} target={r.target} />
            ))}
            {time.hasMinimumHours && time.internalShare > 30 && (
              <div style={{ marginTop: 10 }}>
                <Banner tone="warn">
                  Founder time is drifting away from external proof creation. {time.internalShare}% of this week sits
                  outside counterparty-facing work.
                </Banner>
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
